import React from 'react';
import {
  Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Typography, Divider, Slider, Tooltip, IconButton, CircularProgress,
  Alert, Collapse, Skeleton
} from '@mui/material';
import {
  Close as CloseIcon,
  Help as HelpIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useCreateReadingPracticeForm } from '../../hooks/useCreateReadingPracticeForm';
import MarginBottomContainer from '../../../../components/containers/MarginBottomContainer';
import ElevatedContentCard from '../../../../components/containers/ElevatedContentCard';

const CreateReadingPractice = ({ onCancel, onContentGenerated }) => {
  const {
    creating,
    showSuccess,
    formValues,
    handleChange,
    handleSliderChange,
    handleSubmit,
    setShowSuccess,
    loading,
    error,
    config
  } = useCreateReadingPracticeForm({ onCancel, onContentGenerated });

  return (
    <MarginBottomContainer>
      <ElevatedContentCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" fontWeight="600">
            Create New Reading Practice
          </Typography>
          <IconButton onClick={onCancel} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Collapse in={showSuccess}>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setShowSuccess(false)}>
            Content creation request submitted successfully!
          </Alert>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={100} />
              </Grid>
            </Grid>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="grade-level-label">Grade Level</InputLabel>
                  <Select
                    labelId="grade-level-label"
                    id="gradeLevel"
                    name="gradeLevel"
                    value={formValues.gradeLevel}
                    onChange={handleChange}
                    label="Grade Level"
                    disabled={creating}
                  >
                    {config?.gradeLevels?.map((level) => (
                      <MenuItem key={level} value={level}>
                        Grade {level.charAt(0) + level.slice(1).toLowerCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="topic-category-label">Topic Category</InputLabel>
                  <Select
                    labelId="topic-category-label"
                    id="category"
                    name="category"
                    value={formValues.category}
                    onChange={handleChange}
                    label="Topic Category"
                    disabled={creating}
                  >
                    {config?.readingCategories?.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formValues.category && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {config?.readingCategories?.find(c => c.id === formValues.category)?.description}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="topic"
                  name="topic"
                  label="Topic"
                  value={formValues.topic}
                  onChange={handleChange}
                  disabled={creating}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="paragraphCount"
                  name="paragraphCount"
                  label="Number of Paragraphs"
                  type="number"
                  value={formValues.paragraphCount}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 20 }}
                  disabled={creating}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="paragraph-content-style-label">Paragraph Content Style</InputLabel>
                  <Select
                    labelId="paragraph-content-style-label"
                    id="paragraphContentStyle"
                    name="paragraphContentStyle"
                    value={formValues.paragraphContentStyle}
                    onChange={handleChange}
                    label="Paragraph Content Style"
                    disabled={creating}
                  >
                    {config?.contentStyles?.map((style) => (
                      <MenuItem key={style.name} value={style.name}>
                        {style.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Word Limit: {formValues.wordCount}
                  <Tooltip title="The number of words to include in the fluency passage">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    id="wordCount"
                    name="wordCount"
                    value={formValues.wordCount}
                    onChange={(_, value) => handleSliderChange(value)}
                    min={50}
                    max={500}
                    step={10}
                    marks={[
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                      { value: 200, label: '200' },
                      { value: 300, label: '300' },
                      { value: 400, label: '400' },
                      { value: 500, label: '500' }
                    ]}
                    valueLabelDisplay="auto"
                    disabled={creating}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
              <Button variant="outlined" onClick={onCancel} disabled={creating}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={creating || !formValues.gradeLevel || !formValues.category || !formValues.topic}
                startIcon={creating ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {creating ? 'Creating...' : 'Generate Content'}
              </Button>
            </Box>
          </form>
        )}
      </ElevatedContentCard>
    </MarginBottomContainer>
  );
};

export default CreateReadingPractice;